import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Share2, Link as LinkIcon, MessageCircle, Send, Mail, Copy, CheckCircle2, Users, Search } from 'lucide-react';
import { buildPostShareUrl, sharePostInternal } from '../api/socialActions';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

/**
 * Phase 50 — Share sheet.
 *
 * Two panes:
 *  1) External — Web Share API + explicit WhatsApp / Telegram / Email / Copy link
 *     buttons so users on desktop (no Web Share API) still have a good UX.
 *  2) Internal — search a Nymia user by username and forward the post via
 *     private message. Requires login.
 */
const SharePostDialog = ({ open, onOpenChange, post }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState('external');
  const [query, setQuery] = useState('');
  const [note, setNote] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const shareUrl = useMemo(() => buildPostShareUrl(post?.id), [post?.id]);
  const shareText = useMemo(() => {
    const author = post?.author?.username ? `@${post.author.username}` : 'una Voce Pro';
    return `Guarda questo post di ${author} su Nymia`;
  }, [post?.author?.username]);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setQuery('');
      setNote('');
      setResults([]);
      setTab('external');
    }
  }, [open]);

  useEffect(() => {
    let alive = true;
    if (!open || tab !== 'internal' || !query.trim()) {
      setResults([]);
      return;
    }
    setBusy(true);
    const q = query.trim();
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/users/search', { params: { q, limit: 6 } });
        if (alive) setResults(res.data || []);
      } catch {
        if (alive) setResults([]);
      } finally {
        if (alive) setBusy(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [query, open, tab]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast({ title: 'Impossibile copiare', variant: 'destructive' });
    }
  };

  const openNativeShare = async () => {
    if (!navigator.share) {
      toast({ title: 'Web Share non disponibile', description: 'Usa Copia link o le opzioni qui sotto.' });
      return;
    }
    try {
      await navigator.share({ title: 'Nymia', text: shareText, url: shareUrl });
    } catch {
      // user dismissed — ignore
    }
  };

  const wa = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const tg = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
  const mail = `mailto:?subject=${encodeURIComponent('Nymia')}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;

  const sendInternal = async (username) => {
    if (!post?.id || !username) return;
    setSending(true);
    try {
      await sharePostInternal(post.id, { to_username: username, note });
      toast({
        title: 'Condiviso',
        description: `Post inviato a @${username}.`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Condivisione non riuscita',
        description: err?.response?.data?.detail || 'Riprova più tardi.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="share-post-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 gold-text" /> Condividi post
          </DialogTitle>
          <DialogDescription>
            Diffondi questo momento fuori o dentro Nymia.
          </DialogDescription>
        </DialogHeader>

        <div className="mb-2 grid grid-cols-2 gap-1 rounded-lg border border-border bg-background p-1">
          <button
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${tab === 'external' ? 'bg-secondary gold-text' : 'text-muted-foreground'}`}
            onClick={() => setTab('external')}
            data-testid="share-tab-external"
          >
            <LinkIcon className="mr-1 inline h-3.5 w-3.5" /> Esterno
          </button>
          <button
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${tab === 'internal' ? 'bg-secondary gold-text' : 'text-muted-foreground'}`}
            onClick={() => setTab('internal')}
            data-testid="share-tab-internal"
          >
            <Users className="mr-1 inline h-3.5 w-3.5" /> Su Nymia
          </button>
        </div>

        {tab === 'external' ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={shareUrl}
                className="h-10 bg-background text-xs"
                data-testid="share-link-input"
              />
              <Button
                size="icon"
                onClick={copyLink}
                variant="outline"
                data-testid="share-copy-btn"
                aria-label="Copia link"
              >
                {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>

            <Button
              onClick={openNativeShare}
              className="w-full gold-gradient"
              data-testid="share-native-btn"
            >
              <Share2 className="mr-2 h-4 w-4" /> Condividi con…
            </Button>

            <div className="grid grid-cols-3 gap-2">
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center gap-1 rounded-lg border border-border bg-secondary/50 py-3 text-xs hover:bg-secondary"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="h-5 w-5 text-emerald-500" />
                WhatsApp
              </a>
              <a
                href={tg}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center gap-1 rounded-lg border border-border bg-secondary/50 py-3 text-xs hover:bg-secondary"
                data-testid="share-telegram-btn"
              >
                <Send className="h-5 w-5 text-sky-500" />
                Telegram
              </a>
              <a
                href={mail}
                className="flex flex-col items-center justify-center gap-1 rounded-lg border border-border bg-secondary/50 py-3 text-xs hover:bg-secondary"
                data-testid="share-email-btn"
              >
                <Mail className="h-5 w-5 text-amber-500" />
                Email
              </a>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {!user ? (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-center text-sm">
                Accedi per condividere internamente su Nymia.
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Cerca un utente per username"
                    className="h-10 bg-background pl-9"
                    data-testid="share-user-search"
                  />
                </div>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Aggiungi un messaggio (opzionale)"
                  maxLength={500}
                  className="h-10 bg-background"
                  data-testid="share-user-note"
                />
                <div className="max-h-56 space-y-1 overflow-y-auto">
                  {busy && <p className="py-2 text-center text-xs text-muted-foreground">Cerco…</p>}
                  {!busy && query && results.length === 0 && (
                    <p className="py-2 text-center text-xs text-muted-foreground">Nessun risultato.</p>
                  )}
                  {results.map((u) => (
                    <button
                      key={u.id || u.user_id || u.username}
                      onClick={() => sendInternal(u.username)}
                      disabled={sending}
                      className="flex w-full items-center gap-3 rounded-lg border border-transparent p-2 text-left hover:border-border hover:bg-secondary/60 disabled:opacity-60"
                      data-testid={`share-user-result-${u.username}`}
                    >
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt={u.username} className="h-9 w-9 shrink-0 rounded-full object-cover object-center" />
                      ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-muted-foreground">
                          {(u.username || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-semibold">@{u.username}</p>
                        {u.full_name && (
                          <p className="text-xs text-muted-foreground">{u.full_name}</p>
                        )}
                      </div>
                      <Send className="h-4 w-4 gold-text" />
                    </button>
                  ))}
                </div>
                <p className="text-center text-[11px] text-muted-foreground">
                  Il post verrà inviato come messaggio privato.
                </p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SharePostDialog;
